import { type FC, useCallback, useEffect } from 'react'

import { useIntl } from 'react-intl'

import {
  Button,
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Flex,
  MESSAGE_BOX_VARIANT_DANGER,
  MESSAGE_BOX_VARIANT_HELP,
  MessageBox,
  Modal,
  MODAL_SIZE_MD,
  ModalBody,
  ModalFoot,
  ModalHead,
  SPACING_16,
  SpacingVertical,
  Spinner,
  Text,
  TEXT_VARIANT_STANDARD
} from 'mailup-web-components'

import { beAutomationApiSimulateWorkflow } from 'Api/beAutomationApiTS'

import useAsyncTask from 'Hooks/useAsyncTask'

export interface SimulateResult {
  totalCount?: number
  discarded?: number
}

export const simulateWorkflow = async (idWorkflow: number): Promise<number> => {
  const { Result } = await beAutomationApiSimulateWorkflow(idWorkflow)

  return Result
}

export const extractSimulateResult = (metadata?: string): SimulateResult => {
  if (metadata !== undefined) {
    try {
      const { TotalCount, Discarded } = JSON.parse(metadata)

      return {
        totalCount: TotalCount,
        discarded: Discarded
      }
    } catch (error) {}
  }

  return {
    totalCount: undefined,
    discarded: undefined
  }
}

interface ModalActivateSimulateProps {
  idWorkflow: number
  mode: 'activate' | 'simulate'
  show: boolean
  onClose: () => void | Promise<void>
  onEdit: (idWorkflow: number) => void | Promise<void>
  onActivate: () => void | Promise<void>
  recipientLimit: number
}

const formatter = {
  strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>
}

const ModalActivateSimulate: FC<ModalActivateSimulateProps> = ({
  idWorkflow,
  mode,
  show,
  onClose,
  onEdit,
  onActivate,
  recipientLimit
}) => {
  const intl = useIntl()

  const launchTask = useCallback(async () => {
    return await simulateWorkflow(idWorkflow)
  }, [idWorkflow])

  const { metadata, init, cleanup, status } = useAsyncTask({
    launchTask
  })

  const { totalCount } = extractSimulateResult(metadata)

  useEffect(() => {
    if (show) {
      void init()
    }

    return () => {
      if (show) {
        void cleanup()
      }
    }
  }, [show])

  const InactiveCount =
    status === 'ready' && totalCount !== undefined ? (
      intl.formatNumber(totalCount)
    ) : (
      <Spinner />
    )

  const handleEdit = (): void => {
    void onEdit(idWorkflow)
  }

  return (
    <Modal
      data-mu-test={`modal-${mode}`}
      showClose
      closeOnOverlayClick
      size={MODAL_SIZE_MD}
      show={show}
      onClose={onClose}
    >
      <ModalHead
        title={intl.formatMessage({
          id:
            mode === 'activate'
              ? 'Load.AutomationWorkflow.AttivazioneWorkflow'
              : 'Load.ModalSimulates.Title'
        })}
      />
      <ModalBody>
        <Flex flexDirection='column' gap='24px'>
          <section>
            {status === 'error' && (
              <>
                <MessageBox
                  variant={MESSAGE_BOX_VARIANT_DANGER}
                  data-mu-test='simulate-error'
                >
                  {intl.formatMessage({
                    id: 'Load.AutomationWorkflow.SimulateError'
                  })}
                </MessageBox>
                <SpacingVertical variant={SPACING_16} />
              </>
            )}

            <Text variant={TEXT_VARIANT_STANDARD}>
              {intl.formatMessage(
                { id: 'Load.UnsubscriptionWorkflow.ModalActivateIntro' },
                { N: InactiveCount, ...formatter }
              )}

              {totalCount !== undefined && (
                <>
                  {recipientLimit > 0 && recipientLimit < totalCount && (
                    <Text variant={TEXT_VARIANT_STANDARD}>
                      {intl.formatMessage(
                        {
                          id: 'Load.UnsubscriptionWorkflow.ModalActivateDailyLimitLower'
                        },
                        {
                          LIMIT: intl.formatNumber(recipientLimit),
                          ...formatter
                        }
                      )}
                    </Text>
                  )}

                  {recipientLimit > totalCount && (
                    <Text variant={TEXT_VARIANT_STANDARD}>
                      {intl.formatMessage(
                        {
                          id: 'Load.UnsubscriptionWorkflow.ModalActivateDailyLimitHigher'
                        },
                        {
                          LIMIT: intl.formatNumber(recipientLimit),
                          ...formatter
                        }
                      )}
                    </Text>
                  )}

                  {recipientLimit <= 0 && (
                    <Text variant={TEXT_VARIANT_STANDARD}>
                      {intl.formatMessage(
                        {
                          id: 'Load.UnsubscriptionWorkflow.ModalActivateDailyLimitUnset'
                        },
                        formatter
                      )}
                    </Text>
                  )}
                </>
              )}
            </Text>
          </section>

          {mode === 'activate' && (
            <Text variant={TEXT_VARIANT_STANDARD}>
              {intl.formatMessage(
                {
                  id: 'Load.UnsubscriptionWorkflow.ModalActivateAskConfirm'
                },
                formatter
              )}
            </Text>
          )}

          <Text variant={TEXT_VARIANT_STANDARD}>
            {intl.formatMessage(
              {
                id: 'Load.UnsubscriptionWorkflow.ModalActivateHintEdit'
              },
              formatter
            )}
          </Text>

          <MessageBox variant={MESSAGE_BOX_VARIANT_HELP}>
            {intl.formatMessage(
              {
                id: 'Load.UnsubscriptionWorkflow.ModalActivateInfo'
              },
              {
                PCT: 10
              }
            )}
          </MessageBox>
        </Flex>
      </ModalBody>

      <ModalFoot>
        <Button
          kind={BUTTON_VARIANT_SECONDARY}
          onClick={handleEdit}
          data-mu-test='button-edit'
        >
          {intl.formatMessage({
            id: 'Load.UnsubscriptionWorkflow.ModalActivateEdit'
          })}
        </Button>

        {mode === 'activate' && (
          <Button
            kind={BUTTON_VARIANT_PRIMARY}
            onClick={onActivate}
            data-mu-test='button-activate'
            disabled={status !== 'ready'}
          >
            {intl.formatMessage({
              id: 'Load.AutomationWorkflow.AttivazioneWorkflow'
            })}
          </Button>
        )}

        {mode === 'simulate' && (
          <Button
            kind={BUTTON_VARIANT_PRIMARY}
            onClick={onClose}
            data-mu-test='button-confirm'
          >
            {intl.formatMessage({
              id: 'Load.MailupCommon.Ok'
            })}
          </Button>
        )}
      </ModalFoot>
    </Modal>
  )
}

export default ModalActivateSimulate
